import React, { useState, useEffect } from 'react';
import { PredictionHistoryService } from '../services/firebaseService';
import { format } from 'date-fns';

const PredictionHistory = ({ predictionId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [predictionId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const historyData = await PredictionHistoryService.getPredictionHistory(predictionId);
      setHistory(historyData);
    } catch (err) {
      setError('Failed to load prediction history');
      console.error('Error loading prediction history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    try {
      // Handle Firestore timestamp
      if (timestamp.seconds) {
        return format(new Date(timestamp.seconds * 1000), 'MMM dd, yyyy HH:mm:ss');
      }
      // Handle regular Date object
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch (err) {
      return 'Invalid date';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'text-green-600 bg-green-100';
      case 'update':
        return 'text-blue-600 bg-blue-100';
      case 'delete':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return '➕';
      case 'update':
        return '✏️';
      case 'delete':
        return '🗑️';
      default:
        return '📝';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-survivor-500"></div>
            <span className="ml-2">Loading history...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Prediction History</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No history available for this prediction.
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((entry, index) => (
              <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getActionIcon(entry.action)}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(entry.action)}`}>
                      {entry.action.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-600">
                      by {entry.submittedBy}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatTimestamp(entry.timestamp)}
                  </span>
                </div>

                {entry.reason && (
                  <div className="mb-2">
                    <span className="text-sm font-medium text-gray-700">Reason: </span>
                    <span className="text-sm text-gray-600">{entry.reason}</span>
                  </div>
                )}

                {entry.action === 'update' && entry.oldData && entry.newData && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Before:</h4>
                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <pre className="whitespace-pre-wrap text-xs">
                          {JSON.stringify(entry.oldData, null, 2)}
                        </pre>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">After:</h4>
                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <pre className="whitespace-pre-wrap text-xs">
                          {JSON.stringify(entry.newData, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {entry.action === 'create' && entry.newData && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Initial Data:</h4>
                    <div className="bg-gray-50 p-3 rounded text-sm">
                      <pre className="whitespace-pre-wrap text-xs">
                        {JSON.stringify(entry.newData, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionHistory;

